import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, X } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

// PR-F3 (Step 4) — Flatbed driver pay = fixed salary + per-km over Afaqy km
// (Riyadh drivers). Computed and shown; no payroll side effects.

interface Row {
  driverId: string; driverCode: string; name: string;
  basicSalarySar: number | null; perKmRateSar: number | null;
  monthKm: number; kmPaySar: number; paySar: number;
}

const inputCls = 'h-9 w-28 rounded-md border border-input bg-background px-2 text-sm text-end';
const sar = (n: number | null) => (n != null ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—');

export default function FlatbedDriverPayPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const { data, isLoading } = useQuery({
    queryKey: ['flatbed-driver-pay', month],
    queryFn: async () => (await apiClient.get<{ month: string; items: Row[] }>('/api/v1/flatbed-estimate/driver-pay', { params: { month } })).data.items,
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [eBasic, setEBasic] = useState('');
  const [ePerKm, setEPerKm] = useState('');
  const saveMut = useMutation({
    mutationFn: async (driverId: string) =>
      apiClient.post(`/api/v1/flatbed-estimate/driver-pay/${driverId}`, { basicSalarySar: Number(eBasic || 0), perKmRateSar: Number(ePerKm || 0) }),
    onSuccess: () => { setEditId(null); qc.invalidateQueries({ queryKey: ['flatbed-driver-pay'] }); },
  });

  const rows = data ?? [];
  const total = rows.reduce((a, r) => a + r.paySar, 0);

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="font-display text-lg">Flatbed · Driver pay</div>
          <div className="flex items-center gap-2">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
            <Button variant="ghost" size="sm" onClick={() => { window.location.href = '/dashboard/flatbed'; }} className="gap-2"><ArrowLeft className="h-4 w-4" /> Flatbed</Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8">
        <div className="text-sm text-muted-foreground mb-3">Pay = fixed salary + per-km × this month's Afaqy km. Computed, not a payroll run.</div>
        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-4 py-2">Driver</th>
                <th className="text-end font-medium px-4 py-2">Basic (SAR)</th>
                <th className="text-end font-medium px-4 py-2">Per km (SAR)</th>
                <th className="text-end font-medium px-4 py-2">Month km</th>
                <th className="text-end font-medium px-4 py-2">Km pay</th>
                <th className="text-end font-medium px-4 py-2">Total pay</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">No flatbed drivers found for this project.</td></tr>
              )}
              {rows.map((r) => {
                const editing = editId === r.driverId;
                return (
                  <tr key={r.driverId} className="border-t border-border">
                    <td className="px-4 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground font-mono">{r.driverCode}</div></td>
                    <td className="px-4 py-2 text-end">{editing ? <input className={inputCls} value={eBasic} onChange={(e) => setEBasic(e.target.value)} inputMode="decimal" /> : (r.basicSalarySar != null ? sar(r.basicSalarySar) : <span className="text-amber-500">not set</span>)}</td>
                    <td className="px-4 py-2 text-end">{editing ? <input className={inputCls} value={ePerKm} onChange={(e) => setEPerKm(e.target.value)} inputMode="decimal" /> : (r.perKmRateSar != null ? sar(r.perKmRateSar) : <span className="text-amber-500">not set</span>)}</td>
                    <td className="px-4 py-2 text-end">{r.monthKm.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                    <td className="px-4 py-2 text-end text-muted-foreground">{sar(r.kmPaySar)}</td>
                    <td className="px-4 py-2 text-end font-medium">{sar(r.paySar)}</td>
                    <td className="px-4 py-2 text-end whitespace-nowrap">
                      {editing ? (
                        <div className="inline-flex gap-1">
                          <Button size="sm" className="gap-1" disabled={saveMut.isPending} onClick={() => saveMut.mutate(r.driverId)}><Save className="h-3.5 w-3.5" /> Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => { setEditId(r.driverId); setEBasic(r.basicSalarySar != null ? String(r.basicSalarySar) : ''); setEPerKm(r.perKmRateSar != null ? String(r.perKmRateSar) : ''); }}>Set pay</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="border-t border-border bg-muted/20"><td className="px-4 py-2 font-medium" colSpan={5}>Total (computed)</td><td className="px-4 py-2 text-end font-semibold">{sar(total)}</td><td></td></tr></tfoot>
            )}
          </table>
        </div>
      </main>
    </div>
  );
}
